import { QueryClient } from '@tanstack/react-query';
import { tagQueryKeys } from './keys';
import { UpdateTagInput } from './types';
import { tagAnalysisQueryKeys } from './tag-analysis';
import { taxonomyQueryKeys } from './taxonomy';

export { tagQueryKeys };

const NON_CALC_UPDATE_FIELDS = new Set<keyof UpdateTagInput>([
  'id',
  'documentSuperId',
  'status',
  'validationStatus',
  'notes',
  'lang',
  'hidden',
  'escape',
  'sectionSuperId',
  'pageId',
  'onId',
]);

export const updateAffectsCalculations = (vars: UpdateTagInput): boolean =>
  (Object.keys(vars) as (keyof UpdateTagInput)[]).some((k) => vars[k] !== undefined && !NON_CALC_UPDATE_FIELDS.has(k));

const invalidateDocumentViews = (queryClient: QueryClient, documentSuperId: number): void => {
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tagsByDocument, documentSuperId],
  });
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tagsByDocumentPaginated, documentSuperId],
  });
  queryClient.invalidateQueries({
    queryKey: [tagAnalysisQueryKeys.analysis, documentSuperId],
  });
};

const invalidateDocumentCalculations = (queryClient: QueryClient, documentSuperId: number): void => {
  queryClient.invalidateQueries({
    queryKey: [taxonomyQueryKeys.calculations, documentSuperId],
  });
};

const invalidateDocumentScoped = (queryClient: QueryClient, documentSuperId: number): void => {
  invalidateDocumentViews(queryClient, documentSuperId);
  invalidateDocumentCalculations(queryClient, documentSuperId);
};

export const invalidateOnTagCreate = (queryClient: QueryClient, vars: { documentSuperId?: number; onId?: string | null }): void => {
  if (vars.documentSuperId) {
    invalidateDocumentScoped(queryClient, vars.documentSuperId);
  }
  if (vars.onId) {
    queryClient.invalidateQueries({
      queryKey: [tagQueryKeys.tagsByElement, vars.onId],
    });
  }
};

export const invalidateOnBatchCreate = (queryClient: QueryClient, documentSuperId?: number): void => {
  if (documentSuperId) {
    invalidateDocumentScoped(queryClient, documentSuperId);
  }
};

export const invalidateOnBlockChain = (queryClient: QueryClient, documentSuperId?: number): void => {
  if (documentSuperId) {
    invalidateDocumentScoped(queryClient, documentSuperId);
  }
};

export const invalidateOnContinue = (queryClient: QueryClient, vars: { documentSuperId?: number; onId?: string | null }): void => {
  if (vars.documentSuperId) {
    invalidateDocumentScoped(queryClient, vars.documentSuperId);
  }
  if (vars.onId) {
    queryClient.invalidateQueries({
      queryKey: [tagQueryKeys.tagsByElement, vars.onId],
    });
  }
};

export const invalidateOnTagUpdate = (queryClient: QueryClient, vars: UpdateTagInput): void => {
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tag, vars.id],
  });
  if (vars.documentSuperId) {
    invalidateDocumentViews(queryClient, vars.documentSuperId);
    if (updateAffectsCalculations(vars)) {
      invalidateDocumentCalculations(queryClient, vars.documentSuperId);
    }
  }
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tagsByElement],
  });
};

export const invalidateOnValueSave = (queryClient: QueryClient, documentSuperId?: number): void => {
  if (documentSuperId) {
    invalidateDocumentScoped(queryClient, documentSuperId);
  }
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tagsByElement],
  });
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tag],
  });
};

export const invalidateOnContextRepoint = (queryClient: QueryClient, documentSuperId: number): void => {
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tag],
  });
  invalidateDocumentScoped(queryClient, documentSuperId);
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tagsByElement],
  });
};

export const invalidateOnTagDelete = (queryClient: QueryClient, vars: { id: string; documentSuperId?: number }): void => {
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tag, vars.id],
  });
  if (vars.documentSuperId) {
    invalidateDocumentScoped(queryClient, vars.documentSuperId);
  }
  queryClient.invalidateQueries({
    queryKey: [tagQueryKeys.tagsByElement],
  });
};
